// Modified by AI on 06/12/2026. Edit #1.
import { get, isEmpty } from 'lodash';

import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useContext,
} from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { matchPath } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import CampaignHttpService from '../api/CampaignHttpService';
import Logger from '../services/logger';
import { CAMPAIGN_STATUSES } from '../constants/EmployerCampaignStatuses';
import EmployerContext from './EmployerContext';
import {
    CAMPAIGN_CONTACT_CHANNEL_TYPES, CampaignMessageType, CONTACT_CHANNEL_TYPES,
} from '../constants/ContactChannelTypes';
import {
    getCampaignMessageCompoundChannel,
} from '../modules/employer/position/campaign-details/useCampaignMessageHooks';
import {
    CAMPAIGN_TYPES,
} from '../modules/employer/position/campaign-details/campaignUtils';
import PositionContext from './PositionContext';
import { QUERY_KEYS, FIVE_MINUTES_MS } from '../constants/queryKeys';

const CampaignContext = React.createContext({
    campaign: {},
    refreshCampaignStatus: () => {},
    fetchCampaign: () => {},
});

export const CampaignContextProvider = CampaignContext.Provider;

export const CampaignContextConsumer = CampaignContext.Consumer;

const DEFAULT_CAMPAIGN_STATUS = CAMPAIGN_STATUSES.NOT_STARTED;

// Modified by AI on 06/12/2026. Edit #2.
export const CampaignApp = withRouter(({ children, location }) => {
    const queryClient = useQueryClient();

    const match = matchPath(location.pathname, {
        path: '*/position/:positionId([0-9a-f]{24})/*',
        exact: false,
        strict: false,
    });

    const matchCampaignId = matchPath(location.pathname, {
        path: '*/campaignsActivity/:campaignId([0-9a-f]{24})',
        exact: false,
        strict: false,
    }) || matchPath(location.pathname, {
        path: '*/pcCampaignsActivity/:campaignId([0-9a-f]{24})',
        exact: false,
        strict: false,
    }) || matchPath(location.pathname, {
        path: '*/campaign/:campaignId([0-9a-f]{24})',
        exact: false,
        strict: false,
    });

    const isEmployerCampaignCreateMode = !isEmpty(
        matchPath(location.pathname, {
            path: '/employer/:employerId([0-9a-f]{24})/campaign/create',
            exact: true,
            strict: true,
        }),
    );

    const positionId = get(match, 'params.positionId', null);
    const campaignId = get(matchCampaignId, 'params.campaignId', null);

    const { workflowStep } = useParams();

    const { isAtsEmployer } = useContext(EmployerContext);
    const { atsJobUrl } = useContext(PositionContext);

    const [isCampaignUpdated, setIsCampaignUpdated] = useState(false);

    // Modified by AI on 06/12/2026. Edit #3.
    // Fetch campaign by positionId
    const {
        data: campaignByPosition,
        error: campaignByPositionError,
    } = useQuery({
        queryKey: QUERY_KEYS.campaign.byPosition(positionId),
        queryFn: () => CampaignHttpService.getCampaignByPositionId(positionId),
        enabled: !!positionId,
        staleTime: FIVE_MINUTES_MS,
        gcTime: FIVE_MINUTES_MS,
    });

    // Modified by AI on 06/12/2026. Edit #4.
    // Fetch campaign by campaignId
    const {
        data: campaignById,
        error: campaignByIdError,
    } = useQuery({
        queryKey: QUERY_KEYS.campaign.byId(campaignId),
        queryFn: () => CampaignHttpService.getCampaign(campaignId),
        enabled: !!campaignId,
        staleTime: FIVE_MINUTES_MS,
        gcTime: FIVE_MINUTES_MS,
    });

    // Modified by AI on 06/12/2026. Edit #5.
    // Modified by AI on 07/01/2026. Edit #6.
    // Fetch auto contact availability
    const {
        data: autoContactAvailabilityRaw,
        error: autoContactError,
    } = useQuery({
        queryKey: QUERY_KEYS.campaign.autoContactAvailability(positionId, workflowStep),
        queryFn: () => CampaignHttpService.canAddCandidatesToCampaign(positionId, workflowStep),
        enabled: !!positionId,
        staleTime: FIVE_MINUTES_MS,
        gcTime: FIVE_MINUTES_MS,
    });

    const autoContactAvailability = autoContactAvailabilityRaw ?? {};

    // Error logging via useEffect (React Query v5 pattern)
    useEffect(() => {
        if (campaignByPositionError) {
            Logger.info(campaignByPositionError);
        }
    }, [campaignByPositionError]);

    useEffect(() => {
        if (campaignByIdError) {
            Logger.info(campaignByIdError);
        }
    }, [campaignByIdError]);

    useEffect(() => {
        if (autoContactError) {
            Logger.error(autoContactError);
        }
    }, [autoContactError]);

    // Determine campaign: prioritize positionId fetch, fall back to campaignId fetch
    // On error, set to DEFAULT_CAMPAIGN_STATUS (preserves original behavior)
    const campaign = useMemo(() => {
        if (positionId) {
            if (campaignByPositionError) return DEFAULT_CAMPAIGN_STATUS;
            return campaignByPosition || null;
        }
        if (campaignId) {
            if (campaignByIdError) return DEFAULT_CAMPAIGN_STATUS;
            return campaignById || null;
        }
        return null;
    }, [positionId, campaignId, campaignByPosition, campaignById, campaignByPositionError, campaignByIdError]);

    const campaignStatus = get(campaign, 'status.current', DEFAULT_CAMPAIGN_STATUS);

    const isCampaignActive = campaignStatus === CAMPAIGN_STATUSES.ACTIVE;
    const isCampaignNonStarted = campaignStatus === CAMPAIGN_STATUSES.NOT_STARTED;

    const isAtsJobUrlAvailable = isAtsEmployer
        && (isEmployerCampaignCreateMode || campaign?.type === CAMPAIGN_TYPES.EMPLOYER || !isEmpty(atsJobUrl));

    const availableContactChannels = useMemo(() => CAMPAIGN_CONTACT_CHANNEL_TYPES.filter((contactChannel) => {
        if (campaign?.type === CAMPAIGN_TYPES.SYSTEM) {
            return true;
        }

        if (contactChannel === getCampaignMessageCompoundChannel({
            channel: CONTACT_CHANNEL_TYPES.EMAIL,
            type: CampaignMessageType.applyOnWebsite,
        })) {
            return isAtsJobUrlAvailable;
        }

        return true;
    }), [isAtsJobUrlAvailable, campaign]);

    // Modified by AI on 06/12/2026. Edit #6.
    const fetchCampaign = useCallback(async () => {
        if (positionId) {
            await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.campaign.byPosition(positionId), exact: true });
        }
        if (campaignId) {
            await queryClient.invalidateQueries({ queryKey: QUERY_KEYS.campaign.byId(campaignId), exact: true });
        }
    }, [positionId, campaignId, queryClient]);

    // Modified by AI on 06/12/2026. Edit #7.
    const refreshCampaignStatus = useCallback(() => {
        fetchCampaign();
        if (positionId) {
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.campaign.autoContactAvailability(positionId, workflowStep), exact: true });
        }
    }, [fetchCampaign, positionId, workflowStep, queryClient]);

    return (
        <CampaignContextProvider value={{
            campaign,
            campaignId: campaign?._id,
            campaignStatus,
            isCampaignNonStarted,
            isCampaignActive,
            autoContactAvailability,
            refreshCampaignStatus,
            fetchCampaign,
            availableContactChannels,
            isAtsJobUrlAvailable,
            isCampaignUpdated,
            setIsCampaignUpdated,
        }}>
            {children}
        </CampaignContextProvider>
    );
});

export default CampaignContext;
